// src/components/MuseumExport.jsx
import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "../styles/MuseumExport.css";
import { valuationSummary, valuationFooter } from "../utils/valuationDisclosure";

const MuseumExport = ({ selectedArtworks, collectionData, userData }) => {
  const [exportType, setExportType] = useState("donation"); // 'donation' ou 'loan'
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Configuration du dossier
  const [dossierConfig, setDossierConfig] = useState({
    title: "",
    theme: "",
    curatorText: "",
    museumName: "",
    conservatorName: "",
    donationType: "full", // 'full', 'usufruct', 'nue-propriete'
    taxCredit: "",
    presentationEngagement: false,
    fiscalReduction: "",
    proposedCalendar: {
      signatureDate: "",
      deliveryDate: "",
      inauguralExpo: "",
    },
  });

  // -----------------------------
  // Helpers
  // -----------------------------
  const safeFileName = (s) =>
    String(s || "")
      .replace(/[/\\?%*:|"<>]/g, "-")
      .replace(/\s+/g, " ")
      .trim();

  const fetchImageAsBase64 = async (url) => {
    // robuste: si fetch échoue, on throw et on catch plus haut (sans casser le PDF)
    const response = await fetch(url, { method: "GET", mode: "cors", cache: "no-cache" });
    if (!response.ok) throw new Error(`Image HTTP ${response.status}`);
    const blob = await response.blob();
    if (!String(blob.type || "").toLowerCase().startsWith("image/")) {
      throw new Error("Not an image blob");
    }
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const addPlaceholderBox = (doc, x, y, w, h, label = "Image indisponible") => {
    doc.setDrawColor(210);
    doc.rect(x, y, w, h);
    doc.setTextColor(140);
    doc.setFontSize(10);
    doc.text(label, x + w / 2, y + h / 2, { align: "center" });
    doc.setTextColor(0);
    doc.setDrawColor(0);
  };

  const writePdfBlock = ({ doc, title, text, startY, pageHeight, pageWidth, margin }) => {
    let currentY = startY;
    if (currentY > pageHeight - 40) {
      doc.addPage();
      currentY = 25;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(title, margin, currentY);
    currentY += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(text || "[à compléter]", pageWidth - 2 * margin);
    doc.text(lines, margin, currentY);
    currentY += lines.length * 5 + 8;
    return currentY;
  };


  // -----------------------------
  // DONATION
  // -----------------------------
  const generateDonationDossier = async () => {
    setLoading(true);

    try {
      const artworksSafe = Array.isArray(selectedArtworks) ? selectedArtworks : [];
      const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

      let yPosition = 20;
      const pageHeight = doc.internal.pageSize.getHeight
        ? doc.internal.pageSize.getHeight()
        : doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.getWidth
        ? doc.internal.pageSize.getWidth()
        : doc.internal.pageSize.width;
      const margin = 20;

      // === PAGE 1: COUVERTURE ===
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("PROPOSITION DE DONATION", pageWidth / 2, 80, { align: "center" });

      doc.setFontSize(14);
      doc.setFont("helvetica", "normal");
      doc.text(`Collection ${userData?.name || collectionData?.name || ""}`, pageWidth / 2, 100, {
        align: "center",
      });
      doc.text(`Art ${collectionData?.type || "Contemporain"}`, pageWidth / 2, 110, { align: "center" });
      doc.text(new Date().toLocaleDateString("fr-FR"), pageWidth / 2, 120, { align: "center" });

      // Image principale si dispo
      const cover = artworksSafe[0]?.image_url;
      if (cover) {
        try {
          const imgData = await fetchImageAsBase64(cover);
          doc.addImage(imgData, "JPEG", 60, 140, 90, 90);
        } catch (err) {
          console.error("Erreur chargement image couverture:", err);
          addPlaceholderBox(doc, 60, 140, 90, 90, "Image couverture");
        }
      } else {
        addPlaceholderBox(doc, 60, 140, 90, 90, "Image couverture");
      }

      // === PAGE 2: LETTRE D'INTRODUCTION ===
      doc.addPage();
      yPosition = 30;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Lettre d'introduction", margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text("Madame, Monsieur le Conservateur,", margin, yPosition);
      yPosition += 10;

      const introText =
        dossierConfig.curatorText ||
        `J'ai l'honneur de vous proposer une donation de ma collection personnelle, constituée de ${artworksSafe.length} œuvre${
          artworksSafe.length > 1 ? "s" : ""
        }.

Cette sélection s'inscrit particulièrement bien dans vos collections et enrichirait votre parcours muséal dans le domaine de l'art ${
          collectionData?.type || "contemporain"
        }.`;

      const splitText = doc.splitTextToSize(introText, pageWidth - 2 * margin);
      doc.text(splitText, margin, yPosition);
      yPosition += splitText.length * 6 + 10;

      doc.text("Cordialement,", margin, yPosition);
      yPosition += 8;
      doc.text("[Signature]", margin, yPosition);

      // === PAGE 3: VUE D'ENSEMBLE ===
      doc.addPage();
      yPosition = 30;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Vue d'ensemble de la collection", margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("• Présentation du collectionneur", margin, yPosition);
      yPosition += 8;
      doc.setFont("helvetica", "normal");
      const collectorInfo = `Collectionneur depuis ${userData?.collecting_since || "plusieurs années"}, avec un intérêt particulier pour ${
        collectionData?.focus || "l'art contemporain"
      }.`;
      doc.text(doc.splitTextToSize(collectorInfo, pageWidth - 2 * margin), margin + 5, yPosition);
      yPosition += 15;

      doc.setFont("helvetica", "bold");
      doc.text("• Histoire de la collection", margin, yPosition);
      yPosition += 8;
      doc.setFont("helvetica", "normal");
      doc.text(
        doc.splitTextToSize(collectionData?.history || "Collection constituée avec soin au fil des années.", pageWidth - 2 * margin),
        margin + 5,
        yPosition
      );
      yPosition += 15;

      doc.setFont("helvetica", "bold");
      doc.text("• Axes de collection principaux", margin, yPosition);
      yPosition += 8;
      doc.setFont("helvetica", "normal");
      const axes = Array.isArray(collectionData?.main_themes) ? collectionData.main_themes : ["Art contemporain", "Abstraction", "Figuration"];
      axes.forEach((axe) => {
        doc.text(`  - ${axe}`, margin + 5, yPosition);
        yPosition += 6;
      });
      yPosition += 10;

      doc.setFont("helvetica", "bold");
      doc.text("• Quelques chiffres", margin, yPosition);
      yPosition += 8;
      doc.setFont("helvetica", "normal");
      doc.text(`  - Nombre d'œuvres proposées : ${artworksSafe.length}`, margin + 5, yPosition);
      yPosition += 6;

      const artistCount = new Set(artworksSafe.map((a) => a?.artist).filter(Boolean)).size;
      doc.text(`  - Nombre d'artistes : ${artistCount}`, margin + 5, yPosition);
      yPosition += 6;

      const periods = artworksSafe.map((a) => Number(a?.year)).filter((y) => Number.isFinite(y));
      if (periods.length) {
        const minYear = Math.min(...periods);
        const maxYear = Math.max(...periods);
        doc.text(`  - Périodes : ${minYear} - ${maxYear}`, margin + 5, yPosition);
      }

      // === PAGE 4: SYNTHÈSE (TABLEAU) ===
      doc.addPage();
      yPosition = 30;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Synthèse des œuvres proposées", margin, yPosition);
      yPosition += 10;

      const tableData = artworksSafe.map((artwork) => [
        artwork?.artist || "Artiste inconnu",
        artwork?.title || "Sans titre",
        artwork?.year || "-",
        artwork?.medium || "-",
        artwork?.estimated_value
          ? `${Number(artwork.estimated_value).toLocaleString("fr-FR")} €\n${valuationSummary(artwork, (n) => `${Number(n).toLocaleString("fr-FR")} €`, "fr")}`
          : "-",
      ]);

      // ✅ FIX: autoTable(doc, ...) (au lieu de doc.autoTable)
      autoTable(doc, {
        startY: yPosition + 5,
        head: [["Artiste", "Titre", "Date", "Médium", "Estimation indicative"]],
        body: tableData,
        theme: "plain",
        styles: { fontSize: 9, cellPadding: 3, font: "helvetica" },
        headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        margin: { left: margin, right: margin },
      });

      // === PAGES DÉTAILLÉES : 2 pages par œuvre ===
      for (let i = 0; i < artworksSafe.length; i++) {
        const artwork = artworksSafe[i];

        // Page 1: photo
        doc.addPage();
        yPosition = 25;

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(`Œuvre ${i + 1}/${artworksSafe.length}`, pageWidth / 2, yPosition, { align: "center" });
        yPosition += 10;

        if (artwork?.image_url) {
          try {
            const imgData = await fetchImageAsBase64(artwork.image_url);
            const imgW = 150;
            const imgH = 150;
            const x = (pageWidth - imgW) / 2;
            doc.addImage(imgData, "JPEG", x, yPosition, imgW, imgH);
          } catch (err) {
            console.error(`Erreur image oeuvre ${i + 1}:`, err);
            const imgW = 150;
            const imgH = 150;
            const x = (pageWidth - imgW) / 2;
            addPlaceholderBox(doc, x, yPosition, imgW, imgH, `Image œuvre ${i + 1}`);
          }
        } else {
          const imgW = 150;
          const imgH = 150;
          const x = (pageWidth - imgW) / 2;
          addPlaceholderBox(doc, x, yPosition, imgW, imgH, `Image œuvre ${i + 1}`);
        }

        // Page 2: fiche
        doc.addPage();
        yPosition = 25;

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(artwork?.artist || "Artiste inconnu", margin, yPosition);
        yPosition += 8;

        doc.setFontSize(12);
        doc.setFont("helvetica", "italic");
        doc.text(artwork?.title || "Sans titre", margin, yPosition);
        doc.setFont("helvetica", "normal");
        doc.text(String(artwork?.year || ""), pageWidth - margin, yPosition, { align: "right" });
        yPosition += 8;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`${artwork?.medium || "Technique non spécifiée"}, ${artwork?.dimensions || "Dimensions non spécifiées"}`, margin, yPosition);
        yPosition += 12;

        yPosition = writePdfBlock({
          doc,
          title: "DESCRIPTION",
          text: artwork?.description || "[Paragraphe descriptif de l'œuvre]",
          startY: yPosition,
          pageHeight,
          pageWidth,
          margin,
        });

        // provenance
        const provLines = [];
        if (artwork?.acquisition_date && artwork?.acquisition_source) {
          const yy = new Date(artwork.acquisition_date).getFullYear();
          provLines.push(`- [${Number.isNaN(yy) ? "Année" : yy}] : Acquis auprès de ${artwork.acquisition_source}`);
        }
        if (collectionData?.name) provLines.push(`- [Année] : Collection ${collectionData.name}`);
        provLines.push("- [Année] : Collection actuelle");
        yPosition = writePdfBlock({
          doc,
          title: "PROVENANCE",
          text: provLines.join("\n"),
          startY: yPosition,
          pageHeight,
          pageWidth,
          margin,
        });

        // expositions
        if (Array.isArray(artwork?.exhibitions) && artwork.exhibitions.length > 0) {
          const expoText = artwork.exhibitions
            .slice(0, 8)
            .map((expo) => `- ${expo?.date || "[Date]"} : ${expo?.name || "[Nom exposition]"}, ${expo?.venue || "[Lieu]"}`)
            .join("\n");
          yPosition = writePdfBlock({
            doc,
            title: "EXPOSITIONS",
            text: expoText,
            startY: yPosition,
            pageHeight,
            pageWidth,
            margin,
          });
        }

        // état
        yPosition = writePdfBlock({
          doc,
          title: "ÉTAT DE CONSERVATION",
          text: `${artwork?.condition_report || "[Rapport synthétique]"}\nDernière expertise : ${
            artwork?.last_valuation_date ? new Date(artwork.last_valuation_date).toLocaleDateString("fr-FR") : "[Date]"
          } par ${artwork?.assigned_expert || "[Expert]"}`,
          startY: yPosition,
          pageHeight,
          pageWidth,
          margin,
        });

        // estimation
        const estimationValue = artwork?.estimated_value || artwork?.current_estimated_value || artwork?.valuation_market_value;
        yPosition = writePdfBlock({
          doc,
          title: "ESTIMATION",
          text: `Valeur actuelle indicative : ${estimationValue ? Number(estimationValue).toLocaleString("fr-FR") + " €" : "[Montant]"}${
            artwork?.valuation_source ? `\n(Selon ${artwork.valuation_source})` : ""
          }\n${valuationSummary(artwork, (n) => `${Number(n).toLocaleString("fr-FR")} €`, "fr")}`,
          startY: yPosition,
          pageHeight,
          pageWidth,
          margin,
        });

        // documents (page bonus)
        doc.addPage();
        yPosition = 25;

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("CERTIFICATS & DOCUMENTS", margin, yPosition);
        yPosition += 12;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const docs = [];
        if (artwork?.certificate_auth) docs.push("✓ Certificat d'authenticité joint");
        if (artwork?.invoice) docs.push("✓ Facture d'achat originale");
        if (artwork?.assigned_expert)
          docs.push(
            `✓ Rapport d'expertise ${artwork.assigned_expert} (${artwork?.last_valuation_date ? new Date(artwork.last_valuation_date).toLocaleDateString("fr-FR") : "Date"})`
          );
        if (artwork?.certificate_blockchain) docs.push("✓ Preuve d'authenticité numérique (si applicable)");
        if (!docs.length) docs.push("Documents à fournir sur demande");

        for (const line of docs) {
          doc.text(line, margin, yPosition);
          yPosition += 7;
        }
      }

      // === CONDITIONS DE DONATION ===
      doc.addPage();
      yPosition = 30;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Conditions de donation", margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("CONDITIONS PROPOSÉES", margin, yPosition);
      yPosition += 10;

      doc.setFont("helvetica", "normal");
      const conditions = [
        `• Donation ${
          dossierConfig.donationType === "full"
            ? "pleine propriété"
            : dossierConfig.donationType === "usufruct"
            ? "en usufruit"
            : "en nue-propriété"
        } / Dation`,
        `• Crédit : "${dossierConfig.taxCredit || "Don de [Nom]"}" ou "Collection [nom]"`,
        dossierConfig.presentationEngagement ? "• Engagement de présentation (optionnel)" : "",
        dossierConfig.fiscalReduction ? `• Réduction fiscale applicable : ${dossierConfig.fiscalReduction}` : "",
      ].filter(Boolean);

      conditions.forEach((c) => {
        doc.text(c, margin, yPosition);
        yPosition += 8;
      });

      yPosition += 10;
      doc.setFont("helvetica", "bold");
      doc.text("CALENDRIER PROPOSÉ", margin, yPosition);
      yPosition += 10;

      doc.setFont("helvetica", "normal");
      const calendar = [
        `• ${dossierConfig.proposedCalendar.signatureDate || "[Date]"} : Signature acte de donation`,
        `• ${dossierConfig.proposedCalendar.deliveryDate || "[Date]"} : Remise des œuvres`,
        `• ${dossierConfig.proposedCalendar.inauguralExpo || "[Date]"} : Exposition inaugurale (si applicable)`,
      ];
      calendar.forEach((c) => {
        doc.text(c, margin, yPosition);
        yPosition += 8;
      });

      // === ANNEXES ===
      doc.addPage();
      yPosition = 30;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Annexes", margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("DOCUMENTS JOINTS", margin, yPosition);
      yPosition += 10;

      doc.setFont("helvetica", "normal");
      const annexes = [
        "- Certificats d'authenticité (photocopies)",
        "- Rapports d'expertise complets",
        "- Attestations de provenance",
        "- Photographies haute résolution (USB ou lien de téléchargement)",
      ];
      annexes.forEach((a) => {
        doc.text(a, margin, yPosition);
        yPosition += 8;
      });

      // Mention de prudence sur les valeurs (rapport Artprice 2026)
      yPosition += 6;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.text(doc.splitTextToSize(valuationFooter(artworksSafe, "fr"), pageWidth - 2 * margin), margin, yPosition);

      const fileName = `Dossier_Donation_${safeFileName(collectionData?.name || "Collection")}_${new Date()
        .toISOString()
        .split("T")[0]}.pdf`;
      doc.save(fileName);

      setShowModal(false);
      setLoading(false);
    } catch (error) {
      console.error("Erreur génération DONATION:", error);
      alert("Erreur lors de la génération du dossier. Veuillez réessayer.");
      setLoading(false);
    }
  };

  // -----------------------------
  // LOAN (simple, à enrichir)
  // -----------------------------
  const generateLoanDossier = async () => {
    setLoading(true);

    try {
      const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth
        ? doc.internal.pageSize.getWidth()
        : doc.internal.pageSize.width;

      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("PROPOSITION DE PRÊT POUR EXPOSITION", pageWidth / 2, 80, { align: "center" });

      doc.setFontSize(14);
      doc.setFont("helvetica", "normal");
      doc.text(`Collection ${userData?.name || collectionData?.name || ""}`, pageWidth / 2, 100, { align: "center" });
      doc.text(dossierConfig.theme || "Thème de l'exposition", pageWidth / 2, 110, { align: "center" });
      doc.text(new Date().toLocaleDateString("fr-FR"), pageWidth / 2, 120, { align: "center" });

      const fileName = `Dossier_Pret_${safeFileName(collectionData?.name || "Collection")}_${new Date()
        .toISOString()
        .split("T")[0]}.pdf`;
      doc.save(fileName);

      setShowModal(false);
      setLoading(false);
    } catch (error) {
      console.error("Erreur génération PRÊT:", error);
      alert("Erreur lors de la génération du dossier. Veuillez réessayer.");
      setLoading(false);
    }
  };

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        disabled={!selectedArtworks || selectedArtworks.length === 0}
        style={{
          padding: "12px 24px",
          backgroundColor: selectedArtworks?.length > 0 ? "#000" : "#ccc",
          color: "#fff",
          border: "none",
          borderRadius: "2px",
          fontSize: "14px",
          fontFamily: "Georgia, serif",
          cursor: selectedArtworks?.length > 0 ? "pointer" : "not-allowed",
          transition: "all 0.2s ease",
        }}
      >
        Générer dossier musée ({selectedArtworks?.length || 0} œuvre{selectedArtworks?.length > 1 ? "s" : ""})
      </button>

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "40px",
              borderRadius: "2px",
              maxWidth: "600px",
              width: "90%",
              maxHeight: "85vh",
              overflowY: "auto",
              fontFamily: "Georgia, serif",
            }}
          >
            <h2 style={{ fontSize: "24px", fontWeight: "400", marginBottom: "30px", color: "#000" }}>
              Configuration du dossier
            </h2>

            {/* Type d'export */}
            <div style={{ marginBottom: "25px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Type de dossier
              </label>
              <div style={{ display: "flex", gap: "15px" }}>
                <button
                  onClick={() => setExportType("donation")}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: exportType === "donation" ? "#000" : "#f5f5f5",
                    color: exportType === "donation" ? "#fff" : "#000",
                    border: exportType === "donation" ? "1px solid #000" : "1px solid #ddd",
                    borderRadius: "2px",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Proposition de donation
                </button>
                <button
                  onClick={() => setExportType("loan")}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: exportType === "loan" ? "#000" : "#f5f5f5",
                    color: exportType === "loan" ? "#fff" : "#000",
                    border: exportType === "loan" ? "1px solid #000" : "1px solid #ddd",
                    borderRadius: "2px",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Proposition de prêt
                </button>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {exportType === "donation" ? "Thème de la donation" : "Thème de l'exposition"}
              </label>
              <input
                type="text"
                value={dossierConfig.theme}
                onChange={(e) => setDossierConfig({ ...dossierConfig, theme: e.target.value })}
                placeholder="Ex: Art moderne français"
                style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Nom du musée destinataire
              </label>
              <input
                type="text"
                value={dossierConfig.museumName}
                onChange={(e) => setDossierConfig({ ...dossierConfig, museumName: e.target.value })}
                placeholder="Ex: Musée d'Art Moderne"
                style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Texte curatorial personnalisé
              </label>
              <textarea
                value={dossierConfig.curatorText}
                onChange={(e) => setDossierConfig({ ...dossierConfig, curatorText: e.target.value })}
                placeholder="Texte d'introduction expliquant votre démarche..."
                rows={4}
                style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", resize: "vertical" }}
              />
            </div>

            {exportType === "donation" && (
              <>
                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Type de donation
                  </label>
                  <select
                    value={dossierConfig.donationType}
                    onChange={(e) => setDossierConfig({ ...dossierConfig, donationType: e.target.value })}
                    style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", backgroundColor: "#fff" }}
                  >
                    <option value="full">Donation pleine propriété</option>
                    <option value="usufruct">Donation en usufruit</option>
                    <option value="nue-propriete">Donation en nue-propriété</option>
                  </select>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "flex", alignItems: "center", fontSize: "14px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={dossierConfig.presentationEngagement}
                      onChange={(e) => setDossierConfig({ ...dossierConfig, presentationEngagement: e.target.checked })}
                      style={{ marginRight: "10px" }}
                    />
                    Engagement de présentation (optionnel)
                  </label>
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: "15px", marginTop: "30px", paddingTop: "20px", borderTop: "1px solid #eee" }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={loading}
                style={{ flex: 1, padding: "12px", backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", cursor: loading ? "not-allowed" : "pointer" }}
              >
                Annuler
              </button>
              <button
                onClick={exportType === "donation" ? generateDonationDossier : generateLoanDossier}
                disabled={loading}
                style={{ flex: 1, padding: "12px", backgroundColor: loading ? "#999" : "#000", color: "#fff", border: "none", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", cursor: loading ? "wait" : "pointer" }}
              >
                {loading ? "Génération..." : "Générer le dossier PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MuseumExport;
